import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { onboardingAPI } from '../../utils/api';
import OnboardingProgress from '../../components/OnboardingProgress';
import { ShieldCheck, Sparkles, RefreshCw } from 'lucide-react';

/**
 * iter431 — Talent onboarding hub.
 *
 * Shows the ordered sequence (phone → profile → ID → IDV → agreement) with the
 * exact blocking reason at each step and a one-tap CTA to the current step.
 * Reachable at /talent/onboarding regardless of the hard-gate switch so talent
 * (and admins previewing) can always see where they stand.
 */
export default function TalentOnboarding() {
  const navigate = useNavigate();
  const { onboarding, refreshOnboarding } = useAuth();
  const [state, setState] = useState(onboarding || null);
  const [loading, setLoading] = useState(!onboarding);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const res = await onboardingAPI.getStatus();
      setState(res.data);
    } catch (e) {
      // leave prior state
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onboarding) setState(onboarding);
  }, [onboarding]);

  const goToStep = (step) => {
    if (step?.route) navigate(step.route);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshOnboarding();
    await load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-600" />
      </div>
    );
  }

  if (!state || state.applies === false) {
    return (
      <div className="mx-auto max-w-lg px-4 py-10 text-center">
        <p className="text-slate-600">Onboarding does not apply to this account.</p>
      </div>
    );
  }

  const complete = state.complete === true;
  const current = (state.steps || []).find((s) => s.key === state.current_step);

  return (
    <div data-testid="talent-onboarding-hub" className="mx-auto max-w-lg px-4 py-6 sm:py-10">
      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-600 text-white">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {complete ? "You're all set" : 'Finish getting verified'}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {complete
              ? 'Your account is fully verified. You have full access to the portal.'
              : 'Complete these steps in order to unlock full access and start working events.'}
          </p>
        </div>
      </div>

      {complete ? (
        <div
          data-testid="onboarding-complete-card"
          className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center"
        >
          <Sparkles className="mx-auto h-8 w-8 text-emerald-600" />
          <p className="mt-3 text-base font-semibold text-slate-900">Verification complete</p>
          <p className="mt-1 text-sm text-slate-600">You can now browse gigs, apply and work events.</p>
          <button
            data-testid="onboarding-go-dashboard"
            onClick={() => navigate('/talent/dashboard')}
            className="mt-4 inline-flex items-center rounded-lg bg-cyan-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
          >
            Go to dashboard
          </button>
        </div>
      ) : (
        <>
          {current && (
            <div className="mb-5 rounded-2xl border border-cyan-200 bg-cyan-50/50 p-4">
              <p className="text-xs uppercase font-bold text-cyan-700 tracking-wider">Next step</p>
              <p className="mt-1 text-base font-semibold text-slate-900">{current.label}</p>
              {current.reason && <p className="mt-0.5 text-sm text-slate-600">{current.reason}</p>}
              <button
                data-testid="onboarding-continue-cta"
                onClick={() => goToStep(current)}
                className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-cyan-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
              >
                Continue
              </button>
            </div>
          )}
          <OnboardingProgress steps={state.steps || []} currentStep={state.current_step} onStepAction={goToStep} />
        </>
      )}

      <button
        data-testid="onboarding-refresh"
        onClick={handleRefresh}
        disabled={refreshing}
        className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        Refresh status
      </button>
    </div>
  );
}
